"use client";

import { MouseEvent, UIEvent, useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { Loading } from "react-vant";
import Dialog from "@/components/global/Dialog";
import AddOnTabBar from "@/components/product-detail/AddOnTabBar";
import { AddOnProduct } from "@/types/product";
import AddOnProductCard from "@/components/product-detail/AddOnProductCard";
import addOnApi from "@/service/modules/addOn";
import useCartActions from "@/utils/addToCartAction";
import useAddOnCartItems from "@/utils/useAddOnCartItems";
import { toast } from "@/components/global/Toast";
import styles from "@/styles/product-detail/addon-deals.module.scss";

const RECOMMENDED_DISPLAY_SIZE = 7;
const RECOMMENDED_PROBE_SIZE = RECOMMENDED_DISPLAY_SIZE + 1;
const ADD_ON_PAGE_SIZE = 20;
const DETAIL_ADD_ON_NOTICE_DURATION = 4000;

interface Props {
  visible: boolean;
  onClose: () => void;
  subtotal?: number;
  mainProduct?: {
    productID?: string | number;
    qty: string | number;
  };
  onAddOnSuccess?: (response?: unknown) => void;
}

interface AddOnCategory {
  id: number;
  name: string;
}

const normalizeProduct = (item: Record<string, any>): AddOnProduct => {
  const price = Number(item.addonPrice || item.price || 0);
  const normalPrice = Number(item.normalPrice || item.originalPrice || price);
  const image = item.filename || item.newThumbnail || item.thumbnail || "";

  return {
    ...item,
    productID: Number(item.productID),
    productCode: item.productCode || "",
    enabled: Number(item.enabled || 0),
    terminated: Number(item.terminated || 0),
    discontinued: Number(item.discontinued || 0),
    price,
    normalPrice,
    promotionTypeID: Number(item.promotionTypeID || 0),
    inStock: Number(item.inStock || 0),
    name: item.name || "",
    url: item.url || "",
    categoryID: Number(item.categoryID || 0),
    shortName: item.shortName || "",
    preSales: Number(item.preSales || 0),
    preSalesDate: item.preSalesDate || "",
    thumbnail: item.thumbnail || image,
    smallThumbnail: item.smallThumbnail || image,
    newThumbnail: item.newThumbnail || image,
    filename: image,
    courierID: Number(item.courierID || 0),
    warehouseID: Number(item.warehouseID || 0),
    isNew: Number(item.isNew || 0),
    isGroup: Number(item.isGroup || 0),
    isEmailOnly: Number(item.isEmailOnly || 0),
    freeShipping: Number(item.freeShipping || 0),
    onSale: Number(item.onSale || 0),
    reduced: Number(item.reduced || 0),
    promotion: {
      productID: Number(item.promotion?.productID || item.productID || 0),
      promotionPrice: Number(item.promotion?.promotionPrice || price),
      promotionType: Number(item.promotion?.promotionType || 0),
      normalPrice: Number(item.promotion?.normalPrice || normalPrice),
      description: item.promotion?.description || null,
      endDate: item.promotion?.endDate || "",
      pointRate: item.promotion?.pointRate || "",
      promotionRate: Number(item.promotion?.promotionRate || 0),
    },
    description: Array.isArray(item.description) ? item.description : [],
    shortDescription: item.shortDescription || "",
    isClearance: Number(item.isClearance || 0),
    isDropShipping: Number(item.isDropShipping || 0),
    review: item.review || { avgRank: 0, total: 0, summary: [] },
    addonPrice: price,
    appSelectStatus: Number(item.appSelectStatus || 0),
  };
};

const normalizeCategoryItem = (item: any, index: number): AddOnCategory => {
  const id = Number(
    item?.cateId ?? item?.categoryID ?? item?.categoryId ?? item?.id ?? item?.key ?? index
  );
  return {
    id: Number.isNaN(id) ? index : id,
    name:
      item?.cateName ??
      item?.name ??
      item?.categoryName ??
      item?.title ??
      item?.value ??
      `Category ${index + 1}`,
  };
};

const normalizeCategoryMap = (categoryMap: any): AddOnCategory[] => {
  if (!categoryMap) return [];

  if (Array.isArray(categoryMap)) {
    return categoryMap.map(normalizeCategoryItem);
  }

  if (typeof categoryMap === "object") {
    return Object.entries(categoryMap).map(([key, value], index) => {
      if (value && typeof value === "object") {
        return normalizeCategoryItem({ key, ...(value as object) }, index);
      }

      const id = Number(key);
      return {
        id: Number.isNaN(id) ? index : id,
        name: String(value),
      };
    });
  }

  return [];
};

const normalizeCategories = (payload: any): AddOnCategory[] => {
  const responseData =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const directCategoryMap =
    responseData?.categoryMap ||
    (responseData &&
    !responseData.categories &&
    !responseData.categoryList &&
    !responseData.category &&
    !responseData.tabs
      ? responseData
      : undefined);
  const categoryMapList = normalizeCategoryMap(directCategoryMap);
  if (categoryMapList.length) return categoryMapList;

  const rawCategories =
    responseData?.categories ||
    responseData?.categoryList ||
    responseData?.categoryList?.items ||
    responseData?.category ||
    responseData?.category?.items ||
    responseData?.tabs ||
    [];

  return Array.isArray(rawCategories)
    ? rawCategories.map(normalizeCategoryItem)
    : [];
};

const getProductList = (payload: any) => {
  const list = payload?.data?.list || payload?.data?.items || payload?.data || [];
  return Array.isArray(list) ? list : [];
};

const mergeRecommendedProducts = (
  selectedProduct: AddOnProduct | null,
  redZoneProducts: AddOnProduct[]
) => {
  const nextProducts = selectedProduct ? [selectedProduct] : [];
  const productIDs = new Set(nextProducts.map((item) => item.productID));

  redZoneProducts.forEach((product) => {
    if (productIDs.has(product.productID)) return;
    productIDs.add(product.productID);
    nextProducts.push(product);
  });

  return nextProducts.slice(0, RECOMMENDED_DISPLAY_SIZE);
};

const getNoticeMessage = (response: unknown) => {
  const responseData =
    typeof response === "object" && response !== null && "data" in response
      ? (response as { data?: { notice?: unknown } }).data
      : undefined;
  const notice =
    responseData?.notice ||
    (typeof response === "object" && response !== null && "notice" in response
      ? (response as { notice?: unknown }).notice
      : undefined);

  if (
    notice
    && typeof notice === "object"
    && !Array.isArray(notice)
    && Object.keys(notice).length > 0
  ) {
    const { title, content } = notice as { title?: string; content?: string };
    return [title, content].filter(Boolean).join("\n");
  }

  return "";
};

export default function AddOnProductList({
  visible,
  onClose,
  subtotal,
  mainProduct,
  onAddOnSuccess,
}: Props) {
  const [activeCategoryID, setActiveCategoryID] = useState<number>();
  const [recommendedProducts, setRecommendedProducts] = useState<AddOnProduct[]>([]);
  const [products, setProducts] = useState<AddOnProduct[]>([]);
  const [categories, setCategories] = useState<AddOnCategory[]>([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreRecommendedProducts, setHasMoreRecommendedProducts] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [addingProductIDs, setAddingProductIDs] = useState<Record<number, boolean>>({});
  const { addToCart, batchAddToCart } = useCartActions();
  const getAddOnCartItems = useAddOnCartItems(mainProduct);
  const activeCategoryIndex = categories.findIndex(
    (category) => category.id === activeCategoryID
  );
  const categoryTabs = useMemo(
    () => categories.map((category) => ({ id: category.id, name: category.name })),
    [categories]
  );

  const getSelectedRecommendedProduct = useCallback(async () => {
    try {
      const res = await addOnApi.getSelectedAddOns();
      const selectedList = getProductList(res)
        .map((item: Record<string, any>) => normalizeProduct(item))
        .filter((item: AddOnProduct) => item.productID && item.filename);
      return selectedList[0] || null;
    } catch (error) {
      console.error("Selected add-on data acquisition failed:", error);
      return null;
    }
  }, []);

  const getRecommendedProducts = useCallback(async () => {
    try {
      setRecommendedLoading(true);
      const params: Record<string, number> = { perPage: RECOMMENDED_PROBE_SIZE };
      if (subtotal !== undefined) params.subtotal = subtotal;
      const [selectedProduct, res] = await Promise.all([
        getSelectedRecommendedProduct(),
        addOnApi.getAddOnRedZone(params),
      ]);
      const list = getProductList(res).map((item: Record<string, any>) =>
        normalizeProduct(item)
      );
      const nextHasMoreRecommendedProducts = list.length > RECOMMENDED_DISPLAY_SIZE;
      setHasMoreRecommendedProducts(nextHasMoreRecommendedProducts);
      setRecommendedProducts(mergeRecommendedProducts(selectedProduct, list));
      return nextHasMoreRecommendedProducts;
    } catch (error) {
      console.error("Add-on red zone data acquisition failed:", error);
      setHasMoreRecommendedProducts(false);
      setRecommendedProducts([]);
      return false;
    } finally {
      setRecommendedLoading(false);
    }
  }, [getSelectedRecommendedProduct, subtotal]);

  const getAddOnCategories = useCallback(async () => {
    try {
      const params: Record<string, number> = {};
      if (subtotal !== undefined) params.subtotal = subtotal;
      const res = await addOnApi.getAddOnCateMap(params);
      const nextCategories = normalizeCategories(res?.data);
      setCategories(nextCategories);
      return nextCategories;
    } catch (error) {
      console.error("Add-on category data acquisition failed:", error);
      setCategories([]);
      return [];
    }
  }, [subtotal]);

  const getAddOnProducts = useCallback(
    async (categoryID?: number, nextPage = 1, append = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setProductsLoading(true);
        }

        const params: Record<string, number> = {
          page: nextPage,
          perPage: ADD_ON_PAGE_SIZE,
        };
        if (subtotal !== undefined) params.subtotal = subtotal;
        if (categoryID !== undefined) params.categoryID = categoryID;

        const res = await addOnApi.getAddOnList(params);
        const list = (res?.data?.list || []).map((item: Record<string, any>) =>
          normalizeProduct(item)
        );
        setProducts((prev) => (append ? [...prev, ...list] : list));
        setPage(nextPage);
        setHasMore(list.length === ADD_ON_PAGE_SIZE);
      } catch (error) {
        console.error("Add-on list data acquisition failed:", error);
        if (!append) setProducts([]);
      } finally {
        setProductsLoading(false);
        setLoadingMore(false);
      }
    },
    [subtotal]
  );

  const updateProductStatus = (productID: number) => (item: AddOnProduct) => ({
    ...item,
    appSelectStatus: item.productID === productID ? 1 : 0,
  });

  const handleCategoryClick = (categoryID: number) => {
    setActiveCategoryID(categoryID);
    setPage(1);
    setHasMore(true);
    getAddOnProducts(categoryID);
  };

  const handleCategoryTabChange = (index: number) => {
    const category = categories[index];
    if (!category || category.id === activeCategoryID) return;
    handleCategoryClick(category.id);
  };

  const handleContentScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 80;
    if (
      !isNearBottom ||
      !hasMoreRecommendedProducts ||
      productsLoading ||
      loadingMore ||
      !hasMore
    ) return;
    getAddOnProducts(activeCategoryID, page + 1, true);
  };

  const goToCart = () => {
    onClose();
    window.location.href = "/view-cart";
  };

  const handleViewDetail = (product: AddOnProduct) => {
    if (!product.url) return;
    onClose();
    window.location.href = product.url;
  };

  const handleAddToCart = (
    event: MouseEvent<HTMLDivElement>,
    product: AddOnProduct
  ) => {
    event.stopPropagation();
    if (addingProductIDs[product.productID] || product.appSelectStatus) return;

    setAddingProductIDs((prev) => ({
      ...prev,
      [product.productID]: true,
    }));

    const callback = (response: unknown) => {
      setAddingProductIDs((prev) => {
        const next = { ...prev };
        delete next[product.productID];
        return next;
      });
      if (response === "error") return;
      if (mainProduct?.productID) {
        const noticeMessage = getNoticeMessage(response);
        if (noticeMessage) {
          toast.show(noticeMessage, DETAIL_ADD_ON_NOTICE_DURATION);
          window.setTimeout(goToCart, DETAIL_ADD_ON_NOTICE_DURATION);
          return;
        }

        goToCart();
        return;
      }
      setRecommendedProducts((prev) => prev.map(updateProductStatus(product.productID)));
      setProducts((prev) => prev.map(updateProductStatus(product.productID)));
      onAddOnSuccess?.(response);
      onClose();
    };

    const productList = getAddOnCartItems(product.productID);
    if (productList.length > 1) {
      batchAddToCart(
        event,
        { productList },
        callback
      );
      return;
    }

    addToCart(event, { productID: product.productID, qty: 1, from: 1 }, callback);
  };

  useEffect(() => {
    if (!visible) return;

    const initAddOnProducts = async () => {
      setActiveCategoryID(undefined);
      setPage(1);
      setHasMore(false);
      setCategories([]);
      setProducts([]);
      setAddingProductIDs({});

      const nextHasMoreRecommendedProducts = await getRecommendedProducts();
      if (!nextHasMoreRecommendedProducts) return;

      setHasMore(true);
      const nextCategories = await getAddOnCategories();
      const firstCategory = nextCategories[0];
      if (firstCategory) {
        setActiveCategoryID(firstCategory.id);
        getAddOnProducts(firstCategory.id);
      } else {
        getAddOnProducts(undefined);
      }
    };

    initAddOnProducts();
  }, [visible, getAddOnCategories, getRecommendedProducts, getAddOnProducts]);

  return (
    <Dialog
      visible={visible}
      title=""
      closeable={true}
      onClose={onClose}
      showCancelButton={false}
      showConfirmButton={false}
      dialogContentStyle={{ width: "calc(100vw - 18px)", maxWidth: 430 }}
      contentStyle={{ padding: "20px 9px" }}
    >
      <div className={styles["addon-list-wrapper"]} onScroll={handleContentScroll}>
        <div className={styles["addon-list-header"]}>
          <span className={styles["addon-list-title"]}>Super Value Add-on Deals</span>
        </div>

        <div
          className={styles["recommended-section"]}
          style={{
            background: `url('${process.env.IMAGE_DOMAIN}/addon-deals/recommend-add-on-bg.png') no-repeat center / cover`,
          }}
        >
          <div className={styles["recommended-header"]}>
            <span className={styles["recommended-title"]}>Recommended Add-ons</span>
            {hasMoreRecommendedProducts && (
              <span
                className={styles["recommended-change"]}
                onClick={getRecommendedProducts}
              >
                Change
                <Image
                  src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/change.svg`}
                  alt="change"
                  width={10}
                  height={10}
                />
              </span>
            )}
          </div>

          <div
            className={`${styles["recommended-grid"]} ${
              !hasMoreRecommendedProducts ? styles["recommended-grid-two-column"] : ""
            }`}
          >
            {recommendedLoading ? (
              <div className={styles["addon-loading"]}>
                <Loading color="#D90B17" />
              </div>
            ) : (
              recommendedProducts.map((product) => (
                <AddOnProductCard
                  key={`recommended-${product.productID}`}
                  product={product}
                  variant="ProductItem"
                  productItemSize={
                    hasMoreRecommendedProducts ? "default" : "large"
                  }
                  added={product.appSelectStatus === 1}
                  adding={Boolean(addingProductIDs[product.productID])}
                  onAdd={(nextProduct, event) => handleAddToCart(event, nextProduct)}
                  onViewDetail={handleViewDetail}
                />
              ))
            )}
          </div>
        </div>

        {hasMoreRecommendedProducts && categories.length > 0 && (
          <div className={styles["category-tabs"]}>
            <AddOnTabBar
              tabList={categoryTabs}
              activeTab={activeCategoryIndex}
              tabItemRightMargin="24px"
              onTabChange={handleCategoryTabChange}
            />
          </div>
        )}

        {hasMoreRecommendedProducts && (
          <div className={styles["product-vertical-list"]}>
            {productsLoading ? (
              <div className={styles["addon-loading"]}>
                <Loading color="#D90B17" />
              </div>
            ) : (
              products.map((product) => (
                <AddOnProductCard
                  key={`product-list-${product.productID}`}
                  product={product}
                  variant="ProductCardFullWidth"
                  added={product.appSelectStatus === 1}
                  adding={Boolean(addingProductIDs[product.productID])}
                  onAdd={(nextProduct, event) => handleAddToCart(event, nextProduct)}
                  onViewDetail={handleViewDetail}
                />
              ))
            )}
          </div>
        )}

        {loadingMore && (
          <div className={styles["addon-loading-more"]}>
            <Loading color="#D90B17" />
          </div>
        )}
      </div>
    </Dialog>
  );
}
